import moment from 'moment'
import i18n from '../../resources/locales/i18n'

const CONNECTOR_LABELS = {
  usbc: 'pages.rcDashboard.device.connector.usbc',
  'micro-usb': 'pages.rcDashboard.device.connector.microUsb',
  lightning: 'pages.rcDashboard.device.connector.lightning'
}

const normalizeConnectorKey = connector => {
  if (!connector) {
    return null
  }

  const cleaned = connector
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '')

  if (!cleaned) {
    return null
  }

  if (['usbc', 'usbctypec', 'typec', 'usbtypec', 'usbctc', 'usbct', 'typeusb', 'usbtype'].includes(cleaned)) {
    return 'usbc'
  }

  if (['microusb', 'usbmicrousb', 'microb', 'microusb', 'microbusb', 'microusbtype', 'microusb'].includes(cleaned)) {
    return 'micro-usb'
  }

  if (cleaned.includes('lightning')) {
    return 'lightning'
  }

  return connector.toString().toLowerCase()
}

export const formatDeviceInfo = (rcData, translate = key => (i18n?.t ? i18n.t(key) : key)) => {
  if (!rcData) {
    return null
  }

  const { deviceType, deviceModel, deviceConnector } = rcData
  if (!deviceType && !deviceModel && !deviceConnector) {
    return null
  }

  const connectorKey = normalizeConnectorKey(deviceConnector)
  const connectorLabel =
    (connectorKey && CONNECTOR_LABELS[connectorKey] ? translate(CONNECTOR_LABELS[connectorKey]) : null) ||
    (deviceConnector ? deviceConnector : null)

  if (deviceType === 'ios') {
    if (deviceModel && connectorLabel) {
      return `${deviceModel} (${connectorLabel})`
    }
    if (deviceModel) {
      return deviceModel
    }
    const iphoneLabel = translate('pages.rcDashboard.device.iphone')
    return connectorLabel ? `${iphoneLabel} (${connectorLabel})` : iphoneLabel
  }

  if (deviceType === 'android') {
    const parts = [translate('pages.rcDashboard.device.android')]
    if (deviceModel) {
      parts.push(`- ${deviceModel}`)
    }
    return parts.join(' ')
  }

  if (deviceType === 'other') {
    return translate('pages.rcDashboard.device.other')
  }

  return deviceModel || deviceType || null
}

const parseSubmissionType = ({
  shippingParamProvided,
  approvedParamProvided,
  withShipping,
  approved: isAutoApproval
}) => {
  let shipping = '-'
  let approval = '-'
  if (!shippingParamProvided) {
    shipping = `${shipping} Missing shipping submission data`
  } else {
    shipping = `${shipping} ${withShipping ? 'With Shipping' : 'No Shipping'}`
  }

  if (!approvedParamProvided) {
    approval = `${approval} Missing approval submission data`
  } else {
    approval = `${approval} ${isAutoApproval ? 'Auto Approval' : 'Manual Approval'}`
  }

  return `${shipping};${approval}`
}

export const mapToRCLeadsDashboard = leads => {
  return leads.filter(lead => lead.conversionStatus !== 'denied').map(mapToRcLead)
}

const mapLeadStatus = (lead, rcData) => {
  if (lead.conversionStatus === 'pending') {
    return 'newLead'
  } else if (
    !rcData.withShipping ||
    !rcData?.shippingDetails?.status ||
    rcData.shippingDetails.status === 'delivered' ||
    rcData.shippingDetails.status === 'notRequired'
  ) {
    if (!lead.patient || lead.patient?.grinScans?.items?.length === 0) {
      return 'readyToScan'
    } else {
      return 'scanSent'
    }
  } else if (
    rcData.shippingDetails?.status === 'shipmentRequired' ||
    rcData.shippingDetails?.status === 'processing' ||
    rcData.shippingDetails?.status === 'shipped' ||
    rcData.shippingDetails?.status === 'onHold'
  ) {
    return 'waitingForScope'
  }

  return 'newLead'
}

const getLastUpdatedDate = ({ lead, rcData, audit }) => {
  const firstScanCreatedAt = lead?.patient?.grinScans?.items[0]?.createdAt
  const lastShippingUpdateDate = rcData?.shippingDetails?.lastStatusUpdate
  const lastAuditItem = audit.sort((currAudit, nextAudit) =>
    moment.max(moment(currAudit.time), moment(currAudit.time))
  )[0]
  const leadCreatedAt = lead.updatedAt

  const dates = [firstScanCreatedAt, lastShippingUpdateDate, leadCreatedAt, lastAuditItem?.time]
    .filter(date => !!date)
    .map(date => moment(date))

  return moment.max(dates).toISOString()
}

export const mapToRcLead = lead => {
  const rcData = JSON.parse(lead.rcData || '{}')
  const audit = JSON.parse(lead?.audit || '[]')
  const leadStatus = mapLeadStatus(lead, rcData)
  const leadEmail = lead?.patient?.details?.email || lead.email
  const deviceInfo = formatDeviceInfo(rcData)

  return {
    leadId: lead.id,
    leadObj: {
      id: lead.id,
      _version: lead._version,
      conversionStatus: lead.conversionStatus,
      leadName: `${lead.firstName} ${lead.lastName}`,
      leadNameAndEmail: `${lead.firstName} ${lead.lastName} ${leadEmail}`,
      email: leadEmail,
      submissionType: parseSubmissionType(rcData),
      shippingStatus: rcData.shippingDetails?.status,
      status: leadStatus,
      patient: lead?.patient,
      address: `${rcData.address}, ${rcData.city}, ${rcData.zipcode}`,
      address2: rcData.address2 || '-',
      additionalInfo: rcData.additionalInfo || '-',
      createdAt: moment(lead.createdAt).format('ll'),
      doctorName: lead.doctor.name,
      practiceName: lead.doctor.clinic.practiceName,
      rcData,
      grinPlanKey: lead.doctor?.user?.grinPlanKey,
      statusType: lead.statusType,
      deviceType: rcData.deviceType || null,
      deviceModel: rcData.deviceModel || null,
      deviceConnector: rcData.deviceConnector || null,
      deviceInfo
    },
    age: rcData.ageRange,
    leadName: `${lead.firstName} ${lead.lastName}`,
    email: leadEmail,
    address: `${rcData.address}, ${rcData.city}, ${rcData.zipcode}`,
    address2: rcData.address2 || '-',
    country: rcData.country,
    state: rcData?.state || '-',
    shippingStatus: rcData.shippingDetails?.status,
    additionalInfo: rcData.additionalInfo || '-',
    doctorName: lead.doctor.name,
    practiceName: lead.doctor.clinic.practiceName,
    phone: lead.phone,
    conversionStatus: lead.conversionStatus,
    createdAt: lead.createdAt,
    status: leadStatus,
    audit,
    lastUpdateDate: getLastUpdatedDate({ lead, rcData, audit }),
    leadApprovalDate: rcData.marketingData?.approvalDate || rcData.approvalDate,
    grinPlanKey: lead.doctor?.user?.grinPlanKey,
    statusType: lead.statusType,
    deviceType: rcData.deviceType || null,
    deviceModel: rcData.deviceModel || null,
    deviceConnector: rcData.deviceConnector || null,
    deviceInfo
  }
}
